import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Link, Text, VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';

import {useListInterestAssignmentConditionsQuery} from '@dms/api/core';
import {useDeleteTeamMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, suffixTestId, TestIdProps} from 'shared/web';

interface TeamDeleteBlockedDialogProps extends TestIdProps {
  teamId: string;
  onDeleted: () => void;
}

export function TeamDeleteBlockedDialog(props: TeamDeleteBlockedDialogProps) {
  const [deleteTeam, {isLoading: isDeleting}] = useDeleteTeamMutation();
  const {
    data: conditions,
    isLoading,
    isError,
    isFetching,
  } = useListInterestAssignmentConditionsQuery({teamId: props.teamId}, {refetchOnFocus: true});

  const hasConditionsAssigned = !isLoading && !isNil(conditions) && !isEmpty(conditions);

  const handleDelete = () => {
    deleteTeam({id: props.teamId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
        closeCurrentDialog();
        props.onDeleted();
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={8}>
      <DataStatus isLoading={isLoading} isError={isError}>
        {hasConditionsAssigned ? (
          <VStack spacing={3}>
            <Text size="small">{i18n.t('entity.team.labels.deleteBlockedDescription')}</Text>

            <DataStatus isLoading={isFetching}>
              <VStack spacing={2}>
                {conditions?.map((condition) => (
                  <Link
                    key={condition.id}
                    title={condition.name}
                    href={composePath(settingsRoutes.salesLeadsAssignmentEdit, {
                      params: {id: condition.id},
                    })}
                    target="_blank"
                    size="small"
                    data-testid={suffixTestId(`condition-${condition.id}`, props)}
                  />
                ))}
              </VStack>
            </DataStatus>
          </VStack>
        ) : (
          <Text size="small">{i18n.t('entity.team.labels.allConditionsRemoved')}</Text>
        )}
      </DataStatus>

      <ButtonGroup align="right">
        <Button
          variant="secondary"
          onClick={closeCurrentDialog}
          title={i18n.t('general.actions.close')}
        />
        <Button
          title={i18n.t('entity.team.actions.deleteTeam')}
          variant="danger"
          onClick={handleDelete}
          isLoading={isDeleting}
          isDisabled={hasConditionsAssigned || isLoading || isError}
          data-testid={suffixTestId('deleteTeam', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
